import { OneTrustCategories } from 'onetrust/constants';
import { IS_SSR, isProductionServer } from 'ots-constants';
import { useEffect, useRef, useState } from 'react';
import { Diagnostics } from 'util/diagnostics';
import { useOneTrustInsertAvailableByStatus } from 'util/useOneTrustInsertAvailableByStatus';

const twScriptUrl = 'https://platform.twitter.com/widgets.js';
const twScriptWindowFieldName = 'twttr';

export const canUseDOM = !IS_SSR;

export type TwitterWidgetsApi = any;

export const windowHasTwitter = () => !!(global as any)[twScriptWindowFieldName];
export const getTwitterApi = () => (global as any)[twScriptWindowFieldName]?.widgets;

export const useTwFidgetFactory = () => {
  const wrapper = useOneTrustInsertAvailableByStatus(((oneTrust) => {
    if (oneTrust && !windowHasTwitter()) {
      if (!isProductionServer) {
        Diagnostics.debug('OneTrust?.InsertScript', twScriptUrl);
      }
      oneTrust?.InsertScript(
        twScriptUrl,
        'body',
        () => undefined,
        {},
        OneTrustCategories.SocialMediaCookies,
        true,
      );
    }
  }));
  const [widgets, setWidgets] = useState<TwitterWidgetsApi>(null);
  useEffect(() => {
    if (wrapper) {
      const timer = setInterval(() => {
        const api = getTwitterApi();
        if (api) {
          clearInterval(timer);
          setWidgets(api);
        }
      }, 300);
    }
  }, [wrapper]);

  return widgets;
};

export function removeChildrenWithAttribute(node: HTMLElement, attribute: string) {
  if (node) {
    node.querySelectorAll('*').forEach((child) => {
      if (child.hasAttribute(attribute)) {
        child.remove();
      }
    });
  }
}

function is(x: any, y: any) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  }
  // eslint-disable-next-line no-self-compare
  return x !== x && y !== y;
}
function isShallowEqual(objA: any, objB: any) {
  if (is(objA, objB)) {
    return true;
  }

  if (
    typeof objA !== 'object'
    || objA === null
    || typeof objB !== 'object'
    || objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < keysA.length; i++) {
    if (
      !Object.prototype.hasOwnProperty.call(objB, keysA[i])
      || !is(objA[keysA[i]], objB[keysA[i]])
    ) {
      return false;
    }
  }

  return true;
}

export function useShallowCompareMemoize(value: any) {
  const ref = useRef();
  if (!isShallowEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

export function cloneShallow(value: any) {
  return typeof value === 'object' ? { ...value } : value;
}
